import VisibilityIcon from "@mui/icons-material/Visibility";
import React, { useState } from "react";
import { toast } from "react-hot-toast";

import { getApi } from "../lib/get-api";
import { useAuth } from "../lib/useAuth";
import { Button } from "./ui/Button";

type ViewOnlyBannerProps = {
  /** The project behind this panel. Without it there is nobody to ask. */
  projectId?: string;
  className?: string;
};

/**
 * What a Viewer sees above the grid (MOSA-367 slice 1).
 *
 * The grid is already read-only by then — `Collection` folds the Viewer role
 * into the same `readOnly` flag the shared demo uses, so no edit control is
 * rendered. This banner is the explanation, plus the one way out: ask the
 * owner, who approves it in one click on the members page.
 *
 * The API is the rule either way. A crafted write from a Viewer returns 403
 * `view_only` whether or not this banner is on screen.
 */
export const ViewOnlyBanner: React.FC<ViewOnlyBannerProps> = ({
  projectId,
  className = "",
}) => {
  const { token } = useAuth();
  const [state, setState] = useState<"idle" | "sending" | "requested">("idle");

  const request = async () => {
    if (!projectId) return;
    setState("sending");
    try {
      const api = getApi({ token });
      await (api as any).request({
        path: `/projects/${projectId}/edit-access-requests`,
        method: "POST",
        format: "json",
      });
      setState("requested");
      toast.success("The owner has your request.");
    } catch (error: any) {
      setState("idle");
      toast.error(
        error.response?.data?.message || "Could not send that request",
      );
    }
  };

  return (
    <div
      data-testid="view-only-banner"
      className={`flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-od-border bg-od-surface-2 px-3 py-2 ${className}`}
    >
      <VisibilityIcon
        sx={{ fontSize: 16 }}
        className="shrink-0 text-od-muted"
        aria-hidden="true"
      />
      <p className="m-0 min-w-0 flex-1 text-sm text-od-text">
        You have view-only access
      </p>
      {projectId &&
        (state === "requested" ? (
          <span className="shrink-0 text-xs text-od-muted">
            Edit access requested
          </span>
        ) : (
          <Button
            variant="secondary"
            size="sm"
            loading={state === "sending"}
            onClick={request}
          >
            Request edit access
          </Button>
        ))}
    </div>
  );
};

export default ViewOnlyBanner;
